import { useState } from "react";
import { callLinkKindForStep } from "./index";
import { formatDate, makeSafeT, staffStepStatus } from "./core";
import { staffMutationErrorKey } from "./model";
import { parseValue, type StaffPaneProps } from "./console-types";
import { StaffVideosCard } from "../staff-videos";
import { BotTransferCard } from "./BotTransferCard";
import { AppReleaseCard } from "./AppReleaseCard";
import { MissingOutputNotice } from "./MissingOutputNotice";
import type { StaffDetail } from "./model";
import { isOwnerRoleReason, ownerRoleReasonKey } from "../../shared/owner-role";
// The partner reads a code STATUS, never the unlock_codes UUID (N20).
export function callCodeStateKey(call: { unlockCodeId?: unknown; codeActive?: unknown }): string {
  if (!call.unlockCodeId) return "staffConsole.noCode";
  return call.codeActive ? "staffConsole.codeIssued" : "staffConsole.codeInactive";
}

/**
 * Phase cards (audit V2). Each one answers "what does the partner DO in this
 * step" for exactly one step of the client journey. They read the same
 * resources the client's own surface reads, so the two never disagree.
 */
export function CallPhaseCard(props: StaffPaneProps & { step: string }) {
  const kind = callLinkKindForStep(props.step);
  if (!kind) return null;
  // F81: the step is a video gate. The partner's workspace answers the two
  // real questions - which video the client gets and how far they honestly
  // got (incl. whether the unlock moment already opened the next step). The
  // old link/date/attendance facts belong to the retired group calls.
  return (
    <>
      <article className="staff-card staff-phase-card">
        <h3>{props.t(`staffConsole.callPhase.${kind}`)}</h3>
        <p className="staff-card-hint">{props.t("staffConsole.callPhase.hint")}</p>
      </article>
      <StaffVideosCard
        clientId={props.detail.client.id}
        locale={props.locale}
        t={props.t}
        only={kind === "onboarding" ? "onboarding_video" : "marketing_video"}
      />
    </>
  );
}

export function CopyableLink(props: { href: string; label: string; t: (key: string) => string }) {
  const [copied, setCopied] = useState(false);
  return (
    <span className="copyable-link">
      <a href={props.href} target="_blank" rel="noreferrer">{props.label}</a>
      <button
        className="text-button"
        type="button"
        onClick={() => {
          void navigator.clipboard?.writeText(props.href).then(() => {
            setCopied(true);
            window.setTimeout(() => setCopied(false), 2000);
          });
        }}
      >
        {copied ? props.t("staffConsole.copied") : props.t("staffConsole.copy")}
      </button>
    </span>
  );
}

export function TrainingPhaseCard(props: StaffPaneProps) {
  const trialRow = props.detail.resources.find((row) => String(row.key) === "trial_link");
  const trial = trialRow ? parseValue(trialRow) : {};
  const bot = typeof trial.bot_username === "string" ? trial.bot_username.replace(/^@/, "") : null;
  const trialUrl = typeof trial.url === "string" && trial.url.startsWith("https://") ? trial.url : null;
  // F59: reviewing a Buddy means reading the prompt it runs on. The runner may
  // report the prompt's own address; until it does, the instance detail on the
  // hub is where it lives, and the card SAYS so instead of leaving the partner
  // to hunt through the build log.
  const promptUrl = typeof trial.system_prompt_url === "string" && trial.system_prompt_url.startsWith("https://")
    ? trial.system_prompt_url
    : null;
  const instanceSlug = typeof trial.instance_slug === "string" && trial.instance_slug ? trial.instance_slug : null;
  const step = props.detail.steps.find((row) => row.key === "training");
  const status = step ? staffStepStatus(step) : "locked";
  // The card copy branches on the canonical status. "Alex trénuje" next to a
  // blocked badge told the team to wait for work nobody was doing (audit
  // a2-01, a3-02): the working copy is allowed ONLY in the automation state.
  const hint = status === "blocked"
    ? "staffConsole.trainingPhase.blocked"
    : bot
      ? "staffConsole.trainingPhase.ready"
      : status === "done" || status === "done_ahead"
        ? "staffConsole.trainingPhase.doneNoBot"
        : status === "working"
          ? "staffConsole.trainingPhase.running"
          : "staffConsole.trainingPhase.locked";
  // The whole training lane runs itself now (Pavel 2026-08-05): the run
  // starts on the client's confirm and reports back on its own. The ONE
  // manual gate left is this review, so the card has to say the sequence out
  // loud - check the Buddy, approve the step, and only then does the client
  // get it. A trained instance that nobody approves is the failure this copy
  // exists to prevent.
  const awaitingReview = Boolean(trialUrl) && (status === "working" || status === "blocked");
  const reviewLink = trialUrl ?? (bot ? `https://t.me/${bot}` : null);
  return (
    <article className="staff-card staff-phase-card">
      <h3>{props.t("staffConsole.trainingPhase.title")}</h3>
      <p className="staff-card-hint">{props.t(hint)}</p>
      {/* B2: the blocked step names who finishes it and where, and hands the
          admin the way there. It used to end on "nic dalšího tu dělat
          nemusíš" while the queue told the same partner to finish it. */}
      {status === "blocked" && (
        <MissingOutputNotice
          messageKey="staffConsole.trainingPhase.completionProblem"
          step="training"
          role={props.role}
          t={props.t}
          onOpenSection={props.onOpenSection}
        />
      )}
      {awaitingReview && (
        <ol className="staff-review-sequence">
          <li>{props.t("staffConsole.trainingPhase.reviewStep1")}</li>
          <li>{props.t("staffConsole.trainingPhase.reviewStep2")}</li>
          <li>{props.t("staffConsole.trainingPhase.reviewStep3")}</li>
        </ol>
      )}
      {reviewLink && (
        <p>
          <a className="secondary-button" href={reviewLink} target="_blank" rel="noreferrer">
            {props.t(awaitingReview ? "staffConsole.trainingPhase.reviewBot" : "staffConsole.trainingPhase.openBot")}
          </a>
        </p>
      )}
      {awaitingReview && <TrainingApproval {...props} />}
      {bot && (
        <dl className="staff-definition">
          <div>
            <dt>{props.t("staffConsole.trainingPhase.bot")}</dt>
            <dd><a href={`https://t.me/${bot}`} target="_blank" rel="noreferrer">@{bot}</a></dd>
          </div>
          {(promptUrl || instanceSlug) && (
            <div>
              <dt>{props.t("staffConsole.trainingPhase.systemPrompt")}</dt>
              <dd>
                <a
                  href={promptUrl ?? `https://hub.buddypro.app/instance-creator/${instanceSlug}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {props.t(promptUrl
                    ? "staffConsole.trainingPhase.openSystemPrompt"
                    : "staffConsole.trainingPhase.openSystemPromptInDetail")}
                </a>
              </dd>
            </div>
          )}
        </dl>
      )}
    </article>
  );
}

/**
 * F60 (Pavel 2026-08-05): "Nevím, jestli mám jako parťák možnost schválit
 * toho bota. Zatím tu možnost nevidím." He did not: S08 was admin-only and no
 * surface ever rendered it, so the approval existed only in the raw state
 * editor buried in the service drawer. This is the named action, on the step
 * where the review happens, with the consequence spelled out - approving opens
 * the beta for the client, so it asks once before it fires.
 */
export function TrainingApproval(props: StaffPaneProps) {
  const [state, setState] = useState<"idle" | "confirm" | "sending" | "done" | "error">("idle");
  const step = props.detail.steps.find((row) => row.key === "training");
  const betaStep = props.detail.steps.find((row) => row.key === "beta");
  const marketingStep = props.detail.steps.find((row) => row.key === "marketing_call");
  const trialRow = props.detail.resources.find((row) => String(row.key) === "trial_link");
  const trial = trialRow ? parseValue(trialRow) : {};
  // 🔴 The same trial_link the SERVER asserts for S08 (state-store.ts): an
  // https url, a positive invite limit and the moment it was counted. Checking
  // only `url` would offer a button the transition refuses with a bare 409 -
  // the exact "possibility I cannot see the outcome of" this fix removes.
  const trialReady = typeof trial.url === "string" && trial.url.startsWith("https://")
    && Number.isInteger(trial.invite_limit) && Number(trial.invite_limit) >= 1
    && typeof trial.invite_count_known_at === "string" && Boolean(trial.invite_count_known_at);
  const trainingStatus = step ? staffStepStatus(step) : "locked";
  const betaStatus = betaStep ? staffStepStatus(betaStep) : "locked";
  const marketingStatus = marketingStep ? staffStepStatus(marketingStep) : "locked";
  const ready = trainingStatus === "working" && betaStatus === "locked"
    && marketingStatus === "locked" && trialReady;
  if (!step || !ready) return null;

  async function approve() {
    setState("sending");
    const response = await props.run(
      `/api/staff/clients/${props.detail.client.id}/sections/training/transition`,
      {
        method: "POST",
        body: {
          transitionId: "S08",
          reason: props.t("staffConsole.trainingPhase.approveReason")
        }
      }
    );
    setState(response.ok ? "done" : "error");
  }

  return (
    <div className="training-approval">
      <p className="staff-card-hint">{props.t("staffConsole.trainingPhase.approveHint")}</p>
      {state === "confirm" ? (
        <div className="button-row">
          <button className="primary-button compact-button" type="button" onClick={() => void approve()}>
            {props.t("staffConsole.trainingPhase.approveConfirm")}
          </button>
          <button className="text-button" type="button" onClick={() => setState("idle")}>
            {props.t("common.cancel")}
          </button>
        </div>
      ) : (
        <div className="button-row">
          <button
            className="primary-button compact-button"
            type="button"
            disabled={state === "sending"}
            onClick={() => setState("confirm")}
          >
            {props.t(state === "sending" ? "staffConsole.trainingPhase.approving" : "staffConsole.trainingPhase.approve")}
          </button>
          {state === "done" && <span className="message success-message">{props.t("staffConsole.trainingPhase.approved")}</span>}
          {state === "error" && <span className="message error-message">{props.t("common.error")}</span>}
        </div>
      )}
    </div>
  );
}

/**
 * O4 (audit F115, 2026-08-11): the summary row mapped EVERY state except
 * `transferred` to "Převod běží", so a client whose transfer had failed read
 * "Vlastnictví bota: Převod běží" a hundred pixels above the card saying
 * "Převod skončil chybou. Bot je pořád náš." Same class of lie as F113, on the
 * partner's side.
 *
 * The job row is the truth about the ATTEMPT, the resource about the OWNERSHIP,
 * so both are read: a real transfer wins, then a failure (returned to the
 * client or not), then a client-fault note that put the ball back in the
 * client's hands, then a genuinely running attempt.
 */
export function betaTransferStateKey(input: {
  transferState: string;
  clientFix: boolean;
  job: StaffDetail["botTransfer"];
}): string {
  if (input.transferState === "transferred") return "staffConsole.betaPhase.transferDone";
  if (input.job?.status === "failed") {
    return input.job.returnedAt
      ? "staffConsole.betaPhase.transferReturned"
      : "staffConsole.betaPhase.transferFailed";
  }
  if (input.clientFix) return "staffConsole.betaPhase.transferClientFix";
  if (input.job?.status === "pending" || input.job?.status === "running") {
    return "staffConsole.betaPhase.transferPending";
  }
  // No job at all: a manual S18/S19 handover in flight is still "running", an
  // empty resource is "not started".
  return input.transferState ? "staffConsole.betaPhase.transferPending" : "staffConsole.betaPhase.transferNotStarted";
}

export function BetaPhaseCard(props: StaffPaneProps) {
  const trialRow = props.detail.resources.find((row) => String(row.key) === "trial_link");
  const trial = trialRow ? parseValue(trialRow) : {};
  const transferRow = props.detail.resources.find((row) => String(row.key) === "bot_transfer_state");
  const transferValue = transferRow ? parseValue(transferRow) : {};
  const transfer = String(transferValue.state ?? "");
  const clientFix = Boolean(transferValue.client_fix);
  const inviteLimit = Number(trial.invite_limit ?? 0);
  const step = props.detail.steps.find((row) => row.key === "beta");
  // Done-with-missing-outputs is the canonical `blocked` status since fixr4;
  // gating the alert on `done` silently unplugged it (audit a2-01 class).
  const completionProblem = step && staffStepStatus(step) === "blocked";
  return (
    <>
      <article className="staff-card staff-phase-card">
        <h3>{props.t("staffConsole.betaPhase.title")}</h3>
        {completionProblem && (
          <MissingOutputNotice
            messageKey="staffConsole.betaPhase.completionProblem"
            step="beta"
            role={props.role}
            t={props.t}
            onOpenSection={props.onOpenSection}
          />
        )}
        <dl className="staff-definition">
          <div>
            <dt>{props.t("staffConsole.betaPhase.transfer")}</dt>
            <dd>
              {props.t(betaTransferStateKey({
                transferState: transfer,
                clientFix,
                job: props.detail.botTransfer ?? null
              }))}
            </dd>
          </div>
          <div>
            <dt>{props.t("staffConsole.betaPhase.invites")}</dt>
            <dd>
              {inviteLimit > 0
                ? props.t("staffConsole.betaPhase.inviteLimit").replace("{count}", String(inviteLimit))
                : props.t("staffConsole.betaPhase.noInvites")}
            </dd>
          </div>
        </dl>
      </article>
      {/* F112: the transfer JOB - what the machine tried and how it ended -
          plus the way back into the client's hands. */}
      <BotTransferCard {...props} />
    </>
  );
}

export function PublicationPhaseCard(props: StaffPaneProps) {
  const salespage = props.detail.deliverables
    .filter((row) => String(row.kind) === "salespage")
    .sort((a, b) => Number(b.version ?? 0) - Number(a.version ?? 0))[0];
  const published = salespage
    && (salespage.published === 1 || salespage.published === true);
  const stripeDone = props.detail.milestones.some((row) => String(row.key) === "stripe_receiving");
  // A rail-locked publication step must not claim the client can download or
  // publish; "Zamčeno" next to "odemčené" was two truths on one card (audit
  // a2-03, a2-06 round 5). The canonical status decides which sentences are
  // allowed: under a lock BOTH rows say the actions unlock with the step.
  const step = props.detail.steps.find((row) => row.key === "salespage_publish");
  const status = step ? staffStepStatus(step) : "locked";
  const locked = status === "locked" || status === "done_ahead";
  return (
    <article className="staff-card staff-phase-card">
      <h3>{props.t("staffConsole.publicationPhase.title")}</h3>
      <p className="staff-card-hint">
        {props.t(locked
          ? "staffConsole.publicationPhase.lockedHint"
          : "staffConsole.publicationPhase.hint")}
      </p>
      {locked && published && (
        <p className="staff-material-ahead-note">
          {props.t("partnerOS.workspace.aheadOfUnlock")}
        </p>
      )}
      <dl className="staff-definition">
        <div>
          <dt>{props.t("staffConsole.publicationPhase.page")}</dt>
          <dd>
            {published
              ? props.t(locked
                  ? "staffConsole.publicationPhase.pagePublishedLocked"
                  : "staffConsole.publicationPhase.pagePublished")
              : salespage
                ? props.t(locked
                    ? "staffConsole.publicationPhase.pageReadyLocked"
                    : "staffConsole.publicationPhase.pageReady")
                : props.t("staffConsole.publicationPhase.pageMissing")}
          </dd>
        </div>
        <div>
          <dt>{props.t("staffConsole.publicationPhase.stripe")}</dt>
          <dd>
            {stripeDone
              ? props.t(locked
                  ? "staffConsole.publicationPhase.stripeDoneLocked"
                  : "staffConsole.publicationPhase.stripeDone")
              : props.t("staffConsole.publicationPhase.stripeBlocking")}
          </dd>
        </div>
      </dl>
    </article>
  );
}

export function HandoverPhaseCard(props: StaffPaneProps) {
  const telegramRow = props.detail.resources.find((row) => String(row.key) === "handover_telegram");
  const telegram = telegramRow ? parseValue(telegramRow) : {};
  const username = typeof telegram.username === "string" ? telegram.username.replace(/^@/, "") : "";
  // The delivery route stores `telegram_id`; `my_id` covers historic rows
  // written before the field was renamed. Reading only my_id meant the card
  // said "Zatím chybí" over an ID the client had already sent (F105).
  const myId = typeof telegram.telegram_id === "string" && telegram.telegram_id
    ? telegram.telegram_id
    : typeof telegram.my_id === "string" ? telegram.my_id : "";
  const transferRow = props.detail.resources.find((row) => String(row.key) === "bot_transfer_state");
  const transfer = transferRow ? String(parseValue(transferRow).state ?? "") : "";
  // F105: the S20 close waits for the client's dashboard confirmation, so the
  // card must show it - a refused close with an invisible reason is a mute
  // control.
  const confirmationsRow = props.detail.resources.find((row) => String(row.key) === "step_confirmations");
  const confirmations = confirmationsRow ? parseValue(confirmationsRow) : {};
  const dashboard = confirmations.dashboard;
  const dashboardConfirmed = Boolean(dashboard && typeof dashboard === "object" && !Array.isArray(dashboard)
    && (dashboard as Record<string, unknown>).confirmed === true);
  // F118: the owner role is a FACT somebody has to state. The client's step 3
  // runs on /connectWeb and /myLicense, both OWNER-role commands, so until
  // this is recorded the client is told we are still on it - and the step no
  // longer claims they can carry on.
  const ownerRoleSetAt = typeof telegram.owner_role_set_at === "string" ? telegram.owner_role_set_at : "";
  const ownerRoleJob = props.detail.ownerRole ?? null;
  return (
    <article className="staff-card staff-phase-card">
      <h3>{props.t("staffConsole.handoverPhase.title")}</h3>
      <p className="staff-card-hint">{props.t("staffConsole.handoverPhase.hint")}</p>
      <dl className="staff-definition">
        <div>
          <dt>{props.t("staffConsole.handoverPhase.telegram")}</dt>
          <dd>
            {username
              ? <a href={`https://t.me/${username}`} target="_blank" rel="noreferrer">@{username}</a>
              : <span className="staff-missing">{props.t("staffConsole.handoverPhase.telegramMissing")}</span>}
          </dd>
        </div>
        <div>
          <dt>{props.t("staffConsole.handoverPhase.myId")}</dt>
          <dd>{myId || <span className="staff-missing">{props.t("staffConsole.handoverPhase.myIdMissing")}</span>}</dd>
        </div>
        <div>
          <dt>{props.t("staffConsole.handoverPhase.botOwner")}</dt>
          <dd>
            {transfer === "transferred"
              ? props.t("staffConsole.handoverPhase.botOwnerClient")
              : props.t("staffConsole.handoverPhase.botOwnerUs")}
          </dd>
        </div>
        <div>
          <dt>{props.t("staffConsole.handoverPhase.ownerRole")}</dt>
          <dd>
            {ownerRoleSetAt
              ? props.t("staffConsole.handoverPhase.ownerRoleSet").replace("{date}", formatDate(ownerRoleSetAt, props.locale))
              : <span className="staff-missing">{props.t("staffConsole.handoverPhase.ownerRoleMissing")}</span>}
          </dd>
        </div>
        <div>
          <dt>{props.t("staffConsole.handoverPhase.dashboard")}</dt>
          <dd>
            {dashboardConfirmed
              ? props.t("staffConsole.handoverPhase.dashboardConfirmed")
              : <span className="staff-missing">{props.t("staffConsole.handoverPhase.dashboardMissing")}</span>}
          </dd>
        </div>
      </dl>
      {/* F128: the role sets itself, so the card has to SHOW the run. Without
          this an internal failure reads to the partner as a step where nothing
          has happened yet - the exact hole F112 named on the transfer card -
          and the sentence the verdict was read from lives only in a log on
          another machine. */}
      {!ownerRoleSetAt && ownerRoleJob && (
        <div className="staff-owner-role-job" data-owner-role-job={ownerRoleJob.status}>
          <dl className="staff-definition">
            <div>
              <dt>{props.t("staffConsole.handoverPhase.ownerRoleJob.title")}</dt>
              <dd>
                {props.t(`staffConsole.handoverPhase.ownerRoleJob.${ownerRoleJob.status}`)}
                {ownerRoleJob.attempts > 0 && (
                  <> · {props.t("staffConsole.handoverPhase.ownerRoleJob.attempts")} {ownerRoleJob.attempts}</>
                )}
              </dd>
            </div>
            {ownerRoleJob.status === "failed" && ownerRoleJob.reason && (
              <div>
                <dt>{props.t("staffConsole.handoverPhase.ownerRoleJob.reason")}</dt>
                <dd>
                  {isOwnerRoleReason(ownerRoleJob.reason)
                    ? props.t(ownerRoleReasonKey(ownerRoleJob.reason))
                    : ownerRoleJob.reason}
                  {" · "}
                  {props.t(ownerRoleJob.clientFault
                    ? "staffConsole.handoverPhase.ownerRoleJob.faultClient"
                    : "staffConsole.handoverPhase.ownerRoleJob.faultTeam")}
                </dd>
              </div>
            )}
            {ownerRoleJob.status === "failed" && ownerRoleJob.providerMessage && (
              <div>
                <dt>{props.t("staffConsole.handoverPhase.ownerRoleJob.quote")}</dt>
                <dd><q>{ownerRoleJob.providerMessage}</q></dd>
              </div>
            )}
          </dl>
        </div>
      )}
      {!ownerRoleSetAt && <OwnerRoleAction {...props} hasId={Boolean(myId)} />}
    </article>
  );
}

/**
 * The one control that ends the client's wait (F118).
 *
 * It records that a human set the owner role; it does not set it. The
 * platform's own `/setProOwner` transfers ownership away from our pool account
 * and re-encrypts the instance's API keys under the id it is given, so the act
 * itself stays where a person can look at the number first. Pressing this
 * unlocks the client's step 3 and mails them.
 */
function OwnerRoleAction(props: StaffPaneProps & { hasId: boolean }) {
  const [state, setState] = useState<"idle" | "sending" | "done" | "error">("idle");
  const [errorKey, setErrorKey] = useState("staffConsole.handoverPhase.ownerRoleError");
  // A closed control says why, NEXT to itself: a disabled element takes no
  // pointer, so its title is unreachable (the S2-19 law).
  if (!props.hasId) {
    return <p className="staff-card-hint">{props.t("staffConsole.handoverPhase.ownerRoleNeedsId")}</p>;
  }
  async function confirm() {
    setState("sending");
    const response = await props.run(
      `/api/staff/clients/${props.detail.client.id}/handover/owner-role`,
      { method: "POST" }
    );
    if (response.ok) {
      setState("done");
      await props.refresh();
      return;
    }
    // Refusals travel through an ALLOWLIST: `t` THROWS on an unknown key, and
    // a throw in here would leave the button disabled forever (the F106 law).
    setErrorKey(staffMutationErrorKey(response, response.status === 409
      ? "staffConsole.handoverPhase.ownerRoleNeedsId"
      : "staffConsole.handoverPhase.ownerRoleError"));
    setState("error");
  }
  return (
    <div className="handover-owner-role">
      <p className="staff-card-hint">{props.t("staffConsole.handoverPhase.ownerRoleHint")}</p>
      <button
        className="primary-button compact-button"
        type="button"
        disabled={state === "sending" || state === "done"}
        onClick={() => void confirm()}
      >
        {props.t(state === "sending" ? "staffConsole.handoverPhase.ownerRoleSending" : "staffConsole.handoverPhase.ownerRoleAction")}
      </button>
      {state === "done" && <p className="message success-message" role="status">{props.t("staffConsole.handoverPhase.ownerRoleDone")}</p>}
      {state === "error" && <p className="message error-message" role="alert">{props.t(errorKey)}</p>}
    </div>
  );
}

/**
 * The partner's three app steps (F136b).
 *
 * 🔴 Every card reads `detail.appTrack`, the ONE server derivation, and nothing
 * re-derives an app fact from a raw resource here (the fixr5 law). Before this
 * the questionnaire card read `appWizard`, the release card counted a milestone
 * and the Apple card parsed `app_state` by hand - three readings of one track,
 * and none of them could see the prefill lane at all, which is the thing the
 * partner is actually asked about when a client's app step goes quiet.
 */
export function AppPhaseCard(props: StaffPaneProps & { step: string }) {
  const track = props.detail.appTrack;
  if (props.step === "app_questionnaire") {
    const questionnaire = track?.questionnaire;
    const prefill = questionnaire?.prefill ?? "none";
    return (
      <article className="staff-card staff-phase-card" data-app-phase="questionnaire" data-prefill={prefill}>
        <h3>{props.t("staffConsole.appPhase.questionnaire.title")}</h3>
        <p className="staff-card-hint">
          {props.t(questionnaire?.submitted
            ? "staffConsole.appPhase.questionnaire.submitted"
            : questionnaire && questionnaire.total > 0
              ? "staffConsole.appPhase.questionnaire.progress"
              : "staffConsole.appPhase.questionnaire.waiting")}
          {questionnaire && !questionnaire.submitted && questionnaire.total > 0
            ? ` ${questionnaire.done}/${questionnaire.total}`
            : ""}
        </p>
        {/* The prefill is the team's own lane, so it speaks only while it has
            something to say: a landed or never-owed prefill is not news. */}
        {(prefill === "waiting" || prefill === "late") && (
          <p className={prefill === "late" ? "staff-card-hint is-late" : "staff-card-hint"}>
            {props.t(`staffConsole.appPhase.questionnaire.prefill.${prefill}`)}
          </p>
        )}
      </article>
    );
  }
  if (props.step === "app_release") return <AppReleaseCard {...props} />;
  const apple = track?.apple;
  const registration = apple?.registrationStatus ?? "not_started";
  return (
    <article className="staff-card staff-phase-card" data-app-phase="apple" data-apple-status={registration}>
      <h3>{props.t("staffConsole.appPhase.apple.title")}</h3>
      <p className="staff-card-hint">
        {props.t(registration === "active"
          ? "staffConsole.appPhase.apple.active"
          : registration === "submitted"
            ? "staffConsole.appPhase.apple.submitted"
            : "staffConsole.appPhase.apple.waiting")}
      </p>
      {apple?.confirmed && (
        <p className="staff-card-hint">{props.t("staffConsole.appPhase.apple.confirmed")}</p>
      )}
    </article>
  );
}
